import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { NotificationsProvider } from './context/NotificationsContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import Dashboard from './pages/Dashboard';
import Tenants from './pages/Tenants';
import Properties from './pages/Properties';
import Deposits from './pages/Deposits';
import Operating from './pages/Operating';
import Documents from './pages/Documents';
import Payments from './pages/Payments';
import Impressum from './pages/Impressum';
import Datenschutz from './pages/Datenschutz';
import ForgotPassword from './pages/ForgotPassword';
import LandingPage from './pages/LandingPage';
import Pricing from './pages/Pricing';
import Profile from './pages/Profile';
import MieterRegister from './pages/MieterRegister';
import MieterDashboard from './pages/MieterDashboard';
import Requests from './pages/Requests';
import Tasks from './pages/Tasks';
import PlanSelect from './pages/PlanSelect';
import Vorgaenge from './pages/Vorgaenge';
import Zaehler from './pages/Zaehler';
import Nebenkosten from './pages/Nebenkosten';
import Kontakte from './pages/Kontakte';
import Mietuebersicht from './pages/Mietuebersicht';
import Finanzen from './pages/Finanzen';
import Nachrichten from './pages/Nachrichten';
import Units from './pages/Units';
import ImmobilienType from './pages/ImmobilienType';

function ProtectedRoute({ children, allowExpiredTrial = false }) {
  const { user, loading, profile, subscription } = useAuth();
  if (loading) return <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',color:'#9ca3af'}}>Lädt...</div>;
  if (!user) return <Navigate to="/" replace />;
  if (profile?.role === 'mieter') return <Navigate to="/mieter/dashboard" replace />;

  // Email verification is handled by Supabase auth — no custom OTP check needed

  // Trial expired → force pricing page
  if (!allowExpiredTrial && subscription) {
    const isTrialing = subscription.status === 'trialing';
    const trialOver  = isTrialing && !!subscription.trial_ends_at && new Date(subscription.trial_ends_at) < new Date();
    const noAccess   = subscription.status !== null && !['active','trialing'].includes(subscription.status);
    if (trialOver || noAccess) return <Navigate to="/pricing" replace />;
  }

  return <Layout>{children}</Layout>;
}

// Für Login/Register: nicht eingeloggte User sehen die Seite, eingeloggte werden weitergeleitet
function PublicRoute({ children }) {
  const { user, loading, profile } = useAuth();
  if (loading) return null;
  if (user) {
    if (profile?.role === 'mieter') return <Navigate to="/mieter/dashboard" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

// Mieter-geschützte Route: nur für eingeloggte Mieter
function MieterRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',color:'#9ca3af'}}>Lädt...</div>;
  if (!user) return <Navigate to="/" replace />;
  return children;
}

// Für die Landing Page: immer rendern, eingeloggte User still zum Dashboard weiterleiten
function RootRoute() {
  const { user, loading, profile } = useAuth();
  if (loading) return null;
  if (user) {
    if (profile?.role === 'mieter') return <Navigate to="/mieter/dashboard" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return <LandingPage />;
}

export default function App() {
  return (
    <AuthProvider>
      <NotificationsProvider>
      <ToastProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<RootRoute />} />
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/datenschutz" element={<Datenschutz />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/tenants" element={<ProtectedRoute><Tenants /></ProtectedRoute>} />
          <Route path="/properties" element={<ProtectedRoute><Properties /></ProtectedRoute>} />
          <Route path="/units"      element={<ProtectedRoute><Units /></ProtectedRoute>} />
          <Route path="/operating" element={<ProtectedRoute><Operating /></ProtectedRoute>} />
          <Route path="/payments" element={<ProtectedRoute><Payments /></ProtectedRoute>} />
          <Route path="/deposits" element={<ProtectedRoute><Deposits /></ProtectedRoute>} />
          <Route path="/documents" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
          <Route path="/pricing" element={<ProtectedRoute allowExpiredTrial><Pricing /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/requests" element={<ProtectedRoute><Requests /></ProtectedRoute>} />
          <Route path="/tasks"     element={<ProtectedRoute><Tasks /></ProtectedRoute>} />
          <Route path="/vorgaenge" element={<ProtectedRoute><Vorgaenge /></ProtectedRoute>} />
          <Route path="/zaehler"     element={<ProtectedRoute><Zaehler /></ProtectedRoute>} />
          <Route path="/nebenkosten" element={<ProtectedRoute><Nebenkosten /></ProtectedRoute>} />
          <Route path="/kontakte"       element={<ProtectedRoute><Kontakte /></ProtectedRoute>} />
          <Route path="/mietuebersicht" element={<ProtectedRoute><Mietuebersicht /></ProtectedRoute>} />
          <Route path="/finanzen"       element={<ProtectedRoute><Finanzen /></ProtectedRoute>} />
          <Route path="/nachrichten"    element={<ProtectedRoute><Nachrichten /></ProtectedRoute>} />
          <Route path="/plan-select" element={<MieterRoute><PlanSelect /></MieterRoute>} />
          <Route path="/immobilien/:type" element={<ImmobilienType />} />
          <Route path="/mieter/register" element={<MieterRegister />} />
          <Route path="/mieter/dashboard" element={<MieterRoute><MieterDashboard /></MieterRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </ToastProvider>
      </NotificationsProvider>
    </AuthProvider>
  );
}